import React from "react"
import { Box, Grid } from "@mui/material"
import { makeStyles } from "makeStyles"
import Icon from "@mdi/react"
import {
	mdiPlusCircleOutline,
	mdiRefresh,
	mdiContentSave,
	mdiContentDuplicate,
	mdiShareVariant,
	mdiViewGrid,
	mdiMicrosoftExcel,
} from "@mdi/js"

import StyledLockingSystemCell from "./StyledLockingSystemCell"
import Button from "components/Buttons/Button"

import { useTranslation } from "react-i18next"

import { LockingSystemMatrixType } from "@secureo/common/typings/LockingSystem"

import Simoultaneous from "svg/lockingSystem/einzelschliessend-v2.svg"
import { NEXT_PUBLIC_HOST } from "utils/envVariables"

const useStyles = makeStyles()({
	toolbar: {
		gridColumnStart: 1,
		gridColumnEnd: "keyComponent 1",
		gridRowStart: 1,
		gridRowEnd: "matrixHeader",
		border: "1px solid whitesmoke",
		margin: "0px 30px 30px 0px",
		position: "relative",
	},
	background: {
		borderRadius: 9,
		boxShadow: "5px 5px 5px 0px rgba(0,0,0,0.15)",
		background: "white",
		padding: 15,
	},
})

const LockingSystemMatrixToolbar = ({
	isFrozen,
	isLoading,
	matrixType,
	numberOfFreeIncludedKeys,
	showKeyColumns,
	addDefaultLockingComponent,
	addDefaultKeyComponent,
	setSpecialMatrix,
	saveLockingSystem,
	duplicateLockingSystem,
	toggleShareModalIsOpen,
	resetLockingSystem,
	lockingSystemId,
	locale,
}: Props) => {
	const { classes } = useStyles()
	const { t } = useTranslation("lockingSystem")

	return (
		<div className={classes.toolbar}>
			<div style={{ display: "flex", justifyContent: "space-between" }}>
				<div
					className={classes.background}
					style={{ width: "60%" }}
					id="locking-system-toolbar"
				>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Box style={{ display: "flex", alignItems: "center" }}>
								<button
									disabled={isFrozen}
									style={{
										fontSize: "0.5rem",
										border: "none",
										background: "none",
										cursor: "pointer",
										marginLeft: 10,
									}}
									onClick={resetLockingSystem}
								>
									<Icon path={mdiRefresh} size={1} />
								</button>

								<button
									disabled={isLoading}
									style={{
										fontSize: "0.5rem",
										border: "none",
										background: "none",
										cursor: "pointer",
									}}
									onClick={isFrozen ? duplicateLockingSystem : saveLockingSystem}
								>
									<Icon
										path={isFrozen ? mdiContentDuplicate : mdiContentSave}
										size={1}
									/>
								</button>

								<button
									style={{
										fontSize: "0.5rem",
										border: "none",
										background: "none",
										cursor: "pointer",
									}}
									onClick={toggleShareModalIsOpen}
								>
									<Icon path={mdiShareVariant} size={1} />
								</button>
								<button
									onClick={() =>
										window.open(
											`${NEXT_PUBLIC_HOST}/api/lockingSystem/excel?lockingSystemShoppingListId=${lockingSystemId}&locale=${locale}`,
										)
									}
									disabled={isLoading || !lockingSystemId}
									style={{
										fontSize: "0.5rem",
										border: "none",
										background: "none",
										cursor: "pointer",
									}}
								>
									<Icon path={mdiMicrosoftExcel} size={1} />
								</button>
								<Box mx={1} style={{ borderLeft: "1px solid black", height: 20 }} />

								<button
									style={{
										fontSize: "0.5rem",
										border: "none",
										background: "none",
										cursor: "pointer",
									}}
									onClick={() => setSpecialMatrix("single")}
									disabled={isLoading || isFrozen}
								>
									<Simoultaneous
										style={{
											height: 20,
											width: 20,
											objectFit: "contain",
											marginTop: 2,
										}}
									/>
								</button>

								<button
									style={{
										fontSize: "0.5rem",
										border: "none",
										background: "none",
										cursor: "pointer",
									}}
									onClick={() => setSpecialMatrix("simultaneous")}
									disabled={isLoading || isFrozen}
								>
									<Icon path={mdiViewGrid} size={1} />
								</button>

								<div
									style={{
										marginLeft: "1rem",
									}}
								>
									<strong>
										{matrixType === null ? t("system") : t(matrixType)}
									</strong>

									{numberOfFreeIncludedKeys > 0 && (
										<div style={{ color: "grey" }}>
											Bis zu {numberOfFreeIncludedKeys} Schlüssel kostenlos
										</div>
									)}
								</div>
							</Box>
						</Grid>
					</Grid>
				</div>

				<Box>
					{showKeyColumns && addDefaultKeyComponent && (
						<StyledLockingSystemCell gridRowName="1" gridColumnName="addKeyComponent">
							<div style={{ marginLeft: 5 }} id="add-key">
								<Button
									color="primary"
									disabled={isFrozen || isLoading}
									onClick={() => addDefaultKeyComponent()}
									style={{ maxWidth: 240 }}
								>
									<Icon path={mdiPlusCircleOutline} size={1} />

									<Box ml={1}>Schlüssel hinzufügen</Box>
								</Button>
							</div>
						</StyledLockingSystemCell>
					)}
				</Box>
			</div>

			<Box mt={2}>
				{addDefaultLockingComponent && (
					<StyledLockingSystemCell
						gridRowName="addLockingComponent"
						gridColumnName="position"
					>
						<div
							style={{ marginTop: 7, position: "absolute", bottom: -20 }}
							id="add-lock"
						>
							<Button
								color="primary"
								disabled={isFrozen || isLoading}
								onClick={() => addDefaultLockingComponent()}
								style={{ maxWidth: 220 }}
							>
								<Icon path={mdiPlusCircleOutline} size={1} />
								<Box ml={1}>Zylinder hinzufügen</Box>
							</Button>
						</div>
					</StyledLockingSystemCell>
				)}
			</Box>
		</div>
	)
}

interface Props {
	isFrozen: boolean
	isLoading: boolean
	matrixType: LockingSystemMatrixType
	numberOfFreeIncludedKeys: number
	showKeyColumns: boolean
	lockingSystemId: string
	locale: string
	addDefaultLockingComponent: () => void
	addDefaultKeyComponent: () => void
	setSpecialMatrix: (matrixType: LockingSystemMatrixType) => void
	saveLockingSystem: () => void
	duplicateLockingSystem: () => void
	toggleShareModalIsOpen: () => void
	resetLockingSystem: () => void
}

export default LockingSystemMatrixToolbar
